import React, { useState, useEffect, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import * as pdfjs from "pdfjs-dist";
import "pdfjs-dist/build/pdf.worker.entry";
import "./DownloadPage.css";

const SecurePdfViewer = () => {
  const [searchParams] = useSearchParams();
  const [errorMessage, setErrorMessage] = useState("");
  const [isScreenBlocked, setIsScreenBlocked] = useState(false);
  const [showWarning, setShowWarning] = useState(false);
  const pdfContainerRef = useRef(null);
  const pdfInstance = useRef(null);
  const scaleRef = useRef(1.0);
  const boundHandlersRef = useRef([]);

  const userTag = searchParams.get("user") || `Session ${new Date().toLocaleString()}`;

  // Restrict mobile access
  useEffect(() => {
    if (/Mobi|Android|iPhone|iPad/i.test(navigator.userAgent)) {
      document.body.innerHTML =
        "<h3>Access to this page is restricted on mobile devices.</h3>";
    }
  }, []);

  // Fetch PDF & setup security features
  useEffect(() => {
    const fetchPdf = async () => {
      const encryptedLink = searchParams.get("pdf");
      const password = searchParams.get("password");

      if (!encryptedLink || !password) {
        setErrorMessage("Missing required parameters in the URL.");
        return;
      }

      try {
        const response = await fetch(
          `/api/view-pdf?pdf=${encodeURIComponent(encryptedLink)}&password=${encodeURIComponent(password)}`
        );
        if (response.ok) {
          const blob = await response.blob();
          await renderPdf(blob);
        } else {
          const errorData = await response.json().catch(() => ({}));
          setErrorMessage(errorData.error || "Failed to load the PDF.");
        }
      } catch (error) {
        console.error("Error fetching PDF:", error);
        setErrorMessage("An error occurred while fetching the PDF.");
      }
    };

    fetchPdf();
    setupSecurityFeatures();

    return () => cleanupSecurityFeatures();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // -------------------
  // Security Features
  // -------------------
  const setupSecurityFeatures = () => {
    const addBound = (target, type, handler, opts) => {
      target.addEventListener(type, handler, opts);
      boundHandlersRef.current.push({ target, type, handler, opts });
    };

    // Disable right-click, drag, selection, copy/paste
    ["contextmenu", "selectstart", "dragstart", "copy", "cut", "paste"].forEach((evt) => {
      addBound(document, evt, (e) => {
        e.preventDefault();
        if (evt === "copy") triggerScreenshotWarning();
      });
    });

    // Block printing
    addBound(window, "beforeprint", (e) => {
      e.preventDefault?.();
      triggerBlackScreenOverlay();
      return false;
    });

    // -------------------
    // Ultra-fast Screenshot/WinKey detection
    // -------------------
    const screenshotHandler = (e) => {
      const key = (e.key || "").toLowerCase();

      // Detect screenshot combos
      const isWinShiftS = e.metaKey && e.shiftKey && ["3", "4"].includes(key); // Mac
      const isWindowsScreenshot = key === "printscreen" || (e.metaKey && e.key === "s" && e.shiftKey); // Windows
      const isWindowsKey = key === "meta" || key === "win"; // Windows key press

      if (key === "printscreen" || isWinShiftS || isWindowsScreenshot || isWindowsKey) {
        try {
          navigator.clipboard.writeText(""); // clear clipboard instantly
        } catch {}
        triggerBlackScreenOverlay(); // FAST overlay
        triggerScreenshotWarning();
      }
    };

    // Bind to keydown and keypress for long press detection
    addBound(document, "keydown", screenshotHandler, true);
    addBound(document, "keypress", screenshotHandler, true);
    addBound(document, "keyup", screenshotHandler, true);

    // Hide screen on tab switch
    addBound(document, "visibilitychange", () => {
      if (document.hidden) triggerBlackScreenOverlay();
    });
    addBound(window, "blur", () => triggerBlackScreenOverlay());
  };

  const cleanupSecurityFeatures = () => {
    for (const { target, type, handler, opts } of boundHandlersRef.current) {
      try {
        target.removeEventListener(type, handler, opts);
      } catch {}
    }
    boundHandlersRef.current = [];
    setIsScreenBlocked(false);
    setShowWarning(false);
  };

  // -------------------
  // Helper UI actions
  // -------------------
  const triggerScreenshotWarning = () => {
    setShowWarning(true);
    setTimeout(() => setShowWarning(false), 3000);
  };

  const triggerBlackScreenOverlay = () => {
    setIsScreenBlocked(true);
    setTimeout(() => setIsScreenBlocked(false), 8000);
  };

  // -------------------
  // PDF rendering
  // -------------------
  const renderPdf = async (blob) => {
    const pdfData = await blob.arrayBuffer();
    const pdf = await pdfjs.getDocument({ data: pdfData }).promise;
    pdfInstance.current = pdf;
    await adjustAndRenderPages(pdf);
  };

  const adjustAndRenderPages = useCallback(async (pdf) => {
    const container = pdfContainerRef.current;
    if (!container) return;
    container.innerHTML = "";

    const containerWidth = container.clientWidth || window.innerWidth;
    const firstPage = await pdf.getPage(1);
    const vp = firstPage.getViewport({ scale: 1.0 });
    const scaleFactor = containerWidth / vp.width;
    scaleRef.current = scaleFactor;

    for (let i = 1; i <= pdf.numPages; i++) {
      const canvas = document.createElement("canvas");
      canvas.setAttribute("draggable", "false");
      canvas.style.userSelect = "none";
      canvas.style.pointerEvents = "none";
      container.appendChild(canvas);

      const page = await pdf.getPage(i);
      const viewport = page.getViewport({ scale: scaleRef.current });

      canvas.width = viewport.width;
      canvas.height = viewport.height;

      await page.render({
        canvasContext: canvas.getContext("2d"),
        viewport: viewport,
      }).promise;
    }
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (pdfInstance.current) adjustAndRenderPages(pdfInstance.current);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [adjustAndRenderPages]);

  if (errorMessage) {
    return <div className="error-message">{errorMessage}</div>;
  }

  return (
    <div className="pdf-viewer-container" aria-live="polite">
      {isScreenBlocked && <div className="screen-blocker" role="alert" aria-hidden={!isScreenBlocked} />}
      {showWarning && (
        <div className="screenshot-warning" role="status">
          <p>No screenshots or copying allowed � document protected.</p>
        </div>
      )}
      <div className="watermark">{`Protected � ${userTag}`}</div>
      <div className="pdf-container no-copy" ref={pdfContainerRef} />
    </div>
  );
};

export default SecurePdfViewer;
